import { ReactNode } from 'react';
import { useSubscriptionStatus } from '@/hooks/use-subscription-status';
import { SubscriptionBlockedPage } from './SubscriptionBlockedPage';
import { Loader2 } from 'lucide-react';

interface SubscriptionGuardProps {
  children: ReactNode;
}

/**
 * Guards routes that require an active subscription.
 * Shows a blocking page if the user doesn't have an active subscription
 * or has invoices overdue by more than 5 days.
 */
export function SubscriptionGuard({ children }: Readonly<SubscriptionGuardProps>) {
  const {
    isBlocked,
    blockReason,
    daysOverdue,
    overdueInvoice,
    subscription,
    terminalSubscription,
    isLoading,
  } = useSubscriptionStatus();

  // Show loading state while checking subscription
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center space-y-4">
          <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto" />
          <p className="text-sm text-gray-500">Verificando sua assinatura...</p>
        </div>
      </div>
    );
  }

  // Show blocked page if subscription is not active or payment is overdue
  if (isBlocked && blockReason) {
    return (
      <SubscriptionBlockedPage
        blockReason={blockReason}
        daysOverdue={daysOverdue}
        overdueInvoice={overdueInvoice}
        subscription={subscription}
        terminalSubscription={terminalSubscription}
      />
    );
  }

  // Render children if subscription is valid
  return <>{children}</>;
}

export default SubscriptionGuard;

